import qs from "qs";
import { useCallback, useMemo } from "react";
import {
  generatePath,
  RouteComponentProps,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { ExtractStringRouteParams } from "../types/router";

/** Derived from `useHistory().push()` parameters to avoid direct `history` type import. */
type LocationDescriptorObject = Exclude<
  Parameters<ReturnType<typeof useHistory>["push"]>[0],
  string
>;

/** Constraint for the Options generic (state and/or search). */
export type RouteHelpersOptionsConstraint = {
  state?: Record<string, unknown>;
  search?: Record<string, string | number | boolean | string[]>;
};

/** Configuration object accepted as the second argument of {@link createRouteHelpers}. */
export interface RouteHelpersConfig<
  Options extends RouteHelpersOptionsConstraint
> {
  /** Default values for `state` and/or `search`. Merged (shallow) with per-call options. */
  defaultOptions?: Options;
}

/**
 * Argument tuple combining Params and Options for all 4 cases:
 * - No params, no options → `[]`
 * - No params, with options → `[options?: Options]`
 * - With params, no options → `[params: Params]`
 * - With params, with options → `[params: Params, options?: Options]`
 */
/**
 * Checks whether a Params type represents "no route params" — i.e., either
 * `never` (explicit) or `{}` (inferred from a parameterless path).
 * Index-signature types like `{ [k: string]?: string }` (produced when the
 * path literal degrades to `string`) are also treated as parameterless.
 */
type IsParamless<Params> = [Params] extends [never]
  ? true
  : keyof Params extends never
  ? true
  : string extends keyof Params
  ? true
  : false;

/**
 * Argument tuple combining Params and Options for all 4 cases:
 * - No params, no options → `[]`
 * - No params, with options → `[options?: Options]`
 * - With params, no options → `[params: Params]`
 * - With params, with options → `[params: Params, options?: Options]`
 */
type CombinedArgs<
  Params extends Record<string, unknown>,
  Options extends RouteHelpersOptionsConstraint
> = IsParamless<Params> extends true
  ? [Options] extends [never]
    ? []
    : [options?: Partial<Options>]
  : [Options] extends [never]
  ? [params: Params]
  : [params: Params, options?: Partial<Options>];

/**
 * Matches a path string that contains `:paramName` as a substring.
 */
type PathContainingParam<K extends string> = `${string}:${K}${string}`;

/**
 * Converts a union `A | B` into an intersection `A & B`.
 */
type UnionToIntersection<U> = (
  U extends unknown ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

/**
 * Produces a template literal intersection type requiring the path string
 * to contain `:key` for every key in `Params`. When `Params` has no keys,
 * resolves to `string` (any path is acceptable).
 */
type PathWithParams<Params extends Record<string, unknown>> = [
  keyof Params
] extends [never]
  ? string
  : UnionToIntersection<
      keyof Params extends infer K extends string
        ? PathContainingParam<K>
        : never
    >;

/**
 * Object returned by {@link createRouteHelpers}.
 *
 * @template Params - The route parameter types. By default every parameter is
 *   `string` (inferred from the path literal). When a custom generic is
 *   supplied to `createRouteHelpers`, the custom types are used instead.
 * @template Options - Optional navigation options (`state` and/or `search`).
 *   Defaults to `never` (no options accepted).
 */
export interface RouteHelpersResult<
  Params extends Record<string, unknown>,
  Options extends RouteHelpersOptionsConstraint = never
> {
  /** The original route path string passed to `createRouteHelpers`. */
  path: string;

  /**
   * React hook that returns a navigation callback. The callback calls
   * `history.push` with the generated pathname, and optionally `search`
   * and `state` from the options object.
   *
   * Must be called inside a React component (uses `useHistory` internally).
   *
   * @example
   * const goToDetails = routeHelpersTaxDetails.useGoTo();
   * goToDetails({ id: "abc" });
   *
   * @example
   * const goTo = routeHelpersItems.useGoTo();
   * goTo({ id: "abc" }, { state: { backPath: "/" }, search: { sort: "asc" } });
   */
  useGoTo: () => (...args: CombinedArgs<Params, Options>) => void;

  /**
   * React hook that reads and returns typed route params, location state,
   * and parsed search params from the current URL.
   *
   * Must be called inside a React component rendered under a matching
   * `<Route>`.
   *
   * @example
   * const routeHelpersItems = createRouteHelpers<
   *   { id: string },
   *   { state: { backPath: string }; search: { sort: string } }
   * >("/items/:id");
   *
   * const { params, state, search } = routeHelpersItems.useData();
   * params.id;      // string
   * state.backPath;  // string | undefined
   * search.sort;    // string | undefined
   */
  useData: () => UseParamsResult<Params, Options>;

  /**
   * Builds a `LocationDescriptorObject` (from `history`) by filling in route
   * parameters, search, and state. Useful when you need a location object
   * instead of a plain URL string — e.g. for `<Link to={…}>` or
   * `history.push(locationObj)`.
   *
   * @example
   * // Path with parameters
   * const routeHelpersItems = createRouteHelpers("/items/:id");
   * routeHelpersItems.createLocationObject({ id: "abc" });
   * // { pathname: "/items/abc" }
   *
   * @example
   * // With search and state
   * const routeHelpersItems = createRouteHelpers<
   *   { id: string },
   *   { state: { backPath: string }; search: { sort: string } }
   * >("/items/:id");
   * routeHelpersItems.createLocationObject({ id: "abc" }, { state: { backPath: "/" }, search: { sort: "asc" } });
   * // { pathname: "/items/abc", search: "sort=asc", state: { backPath: "/" } }
   */
  createLocationObject: (
    ...args: CombinedArgs<Params, Options>
  ) => LocationDescriptorObject;

  /**
   * Phantom (type-only) field that exposes the route params type.
   * Never exists at runtime — use it only in type positions.
   *
   * @example
   * const routeHelpersTaxDetails = createRouteHelpers("/member/taxes/details/:id");
   * type Params = (typeof routeHelpersTaxDetails)["Params"]; // { id: string }
   */
  Params: Params;

  /**
   * Phantom (type-only) field that exposes the options type.
   * Never exists at runtime — use it only in type positions.
   */
  Options: Options;
}

/** Maps every value in a params record to `string`. */
type StringifyParams<T extends Record<string, unknown>> = {
  [K in keyof T]: string;
};

/** Maps search option values to `string | undefined` (all URL search values are strings). */
type StringifySearch<T extends Record<string, unknown>> = {
  [K in keyof T]: string | undefined;
};

/** Makes every value in a record `| undefined` (state may not be present at runtime). */
type Undefinable<T extends Record<string, unknown>> = {
  [K in keyof T]: T[K] | undefined;
};

/**
 * Return type for {@link RouteHelpersResult.useData}.
 *
 * Composes route params, location state, and parsed search params into a
 * single typed object. All three fields are always present and always
 * objects, so they can be destructured directly. Individual values within
 * `state` and `search` are `| undefined` because they may not be present
 * at runtime.
 */
export type UseParamsResult<
  Params extends Record<string, unknown>,
  Options extends RouteHelpersOptionsConstraint
> = {
  params: StringifyParams<Params>;
} & ([Options] extends [never]
  ? { state: Record<string, never>; search: Record<string, never> }
  : (Options extends { state: infer S extends Record<string, unknown> }
      ? { state: Undefinable<S> }
      : { state: Record<string, never> }) &
      (Options extends { search: infer Q extends Record<string, unknown> }
        ? { search: StringifySearch<Q> }
        : { search: Record<string, never> }));

/**
 * Utility type that derives `RouteComponentProps` from a `RouteHelpersResult`,
 * so route components can be typed without manually extracting `Params`.
 *
 * Accepts both default (all-string) and custom-typed paths. When the path
 * uses custom non-string types (e.g. `number`), param values are mapped
 * back to `string` because React Router always returns strings from the URL.
 *
 * @example
 * const routeHelpersTaxDetails = createRouteHelpers("/member/taxes/details/:id");
 *
 * const DetailsPage: React.FC<RoutePropsFromRouteHelpers<typeof routeHelpersTaxDetails>> =
 *   ({ match }) => {
 *     match.params.id; // string
 *   };
 */
export type RoutePropsFromRouteHelpers<
  T extends { Params: Record<string, unknown> }
> = RouteComponentProps<StringifyParams<T["Params"]>>;

/**
 * Extracts the route params type from a `RouteHelpersResult` for use with
 * `useRouteMatch`. All param values are `string` (as returned by React
 * Router at runtime), even when the path was created with custom types.
 *
 * @example
 * const routeHelpersTaxDetails = createRouteHelpers("/member/taxes/details/:id");
 *
 * const match = useRouteMatch<RouteMatchParamsFromRouteHelpers<typeof routeHelpersTaxDetails>>(
 *   routeHelpersTaxDetails.path
 * );
 * match?.params.id; // string
 */
export type RouteMatchParamsFromRouteHelpers<
  T extends { Params: Record<string, unknown> }
> = StringifyParams<T["Params"]>;

/**
 * Parses a URL search string into an object with `string | undefined` values.
 * Extracts every key present in the query string.
 */
function parseSearchParams(
  searchString: string
): Record<string, string | undefined> {
  return qs.parse(searchString, { ignoreQueryPrefix: true }) as Record<
    string,
    string | undefined
  >;
}

/**
 * Creates a set of type-safe helpers for a given route path.
 *
 * Extracts route parameters from the path literal and provides:
 * - `path`                — the raw path string (for `<Route path={…}>`)
 * - `createLocationObject` — builds a `LocationDescriptorObject`
 * - `useGoTo`              — React hook returning a `history.push` callback
 * - `useData`              — React hook returning typed params, state, and search
 * - `Params`               — phantom type for extracted route params
 * - `Options`              — phantom type for navigation options (state/search)
 *
 * **Naming convention:** use `routeHelpers[Entity]` for the returned object,
 * e.g. `routeHelpersTaxDetails`, `routeHelpersPayeeOnboarding`.
 *
 * @example
 * // Default — params inferred as string from the path literal
 * export const routeHelpersTaxesConfirmation = createRouteHelpers(
 *   "/member/taxes/schedule-payment/confirmation/:pendingWithholdingId"
 * );
 *
 * // With typed options (state + search)
 * export const routeHelpersConfirmation = createRouteHelpers<
 *   { pendingWithholdingId: string },
 *   { state: { backPath: string }; search: { sort: "asc" | "desc" } }
 * >("/member/taxes/schedule-payment/confirmation/:pendingWithholdingId");
 *
 * // With default options — applied to every createLocationObject / useGoTo call
 * export const routeHelpersItems = createRouteHelpers<
 *   { id: string },
 *   { search: { sort: string } }
 * >("/items/:id", { defaultOptions: { search: { sort: "asc" } } });
 * routeHelpersItems.createLocationObject({ id: "abc" });
 * // { pathname: "/items/abc", search: "sort=asc" }
 */
export function createRouteHelpers<
  Params extends Record<string, unknown> = never,
  Options extends RouteHelpersOptionsConstraint = never,
  P extends string = string
>(
  path: P & ([Params] extends [never] ? unknown : PathWithParams<Params>),
  config?: RouteHelpersConfig<Options>
): [Params] extends [never]
  ? RouteHelpersResult<ExtractStringRouteParams<P>, Options>
  : RouteHelpersResult<Params, Options> {
  const pattern: string = path;
  const hasRouteParams = /:[a-zA-Z]/.test(pattern);
  const defaults = config?.defaultOptions;

  const mergeOptions = (
    options: RouteHelpersOptionsConstraint | undefined
  ): RouteHelpersOptionsConstraint | undefined => {
    if (!defaults && !options) return undefined;
    return {
      state: { ...defaults?.state, ...options?.state },
      search: { ...defaults?.search, ...options?.search }
    };
  };

  const createLocationObject = (
    ...args: unknown[]
  ): LocationDescriptorObject => {
    let params: Record<string, unknown> | undefined;
    let options: RouteHelpersOptionsConstraint | undefined;

    if (hasRouteParams) {
      params = args[0] as Record<string, unknown> | undefined;
      options = args[1] as RouteHelpersOptionsConstraint | undefined;
    } else {
      options = args[0] as RouteHelpersOptionsConstraint | undefined;
    }

    const merged = mergeOptions(options);

    const pathname = generatePath(
      pattern,
      params as Record<string, string> | undefined
    );

    const search = merged?.search ? qs.stringify(merged.search) : "";

    return {
      pathname,
      ...(search ? { search } : undefined),
      ...(merged?.state ? { state: merged.state } : undefined)
    };
  };

  const useGoTo = () => {
    const history = useHistory();
    return useCallback(
      (...args: unknown[]) => {
        history.push(createLocationObject(...args));
      },
      [history]
    );
  };

  const useData = () => {
    const match = useRouteMatch<Record<string, string>>(pattern);
    const location = useLocation<Record<string, unknown> | undefined>();

    const params = match?.params;
    const locationState = location.state;
    const locationSearch = location.search;

    return useMemo(
      () => ({
        params: (params ?? {}) as Record<string, string>,
        state: (locationState ?? {}) as Record<string, unknown>,
        search: parseSearchParams(locationSearch)
      }),
      [params, locationState, locationSearch]
    );
  };

  type Result = [Params] extends [never]
    ? RouteHelpersResult<ExtractStringRouteParams<P>, Options>
    : RouteHelpersResult<Params, Options>;

  return {
    path,
    createLocationObject,
    useGoTo,
    useData
  } as unknown as Result;
}
